import React, { useRef, useState, useEffect } from "react";
import { Form, Button, message } from "antd";
import { UserOutlined } from "@ant-design/icons";
import AvatarEditor from "react-avatar-editor";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { CUSTOM_COLORS } from "@/shared/theme/colorUtils";
import { AuthLayout } from "@/shared/layout";
import { authenticationService } from "@/api/authentication";
import { userService } from "@/api/user";

// Styled Components
const UpdateContainer = styled.div`
	text-align: center;
	padding: 1rem 0;
`;

const AvatarSection = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 1rem;
	margin-bottom: 2rem;
`;

const AvatarEditorWrapper = styled.div`
	position: relative;
	width: 120px;
	height: 120px;
	border-radius: 50%;
	overflow: hidden;
	border: 3px solid #f0f0f0;
	cursor: pointer;
	transition: border-color 0.3s ease;

	&:hover {
		border-color: ${CUSTOM_COLORS.PRIMARY};
	}

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
`;

const DefaultAvatar = styled.div`
	width: 100%;
	height: 100%;
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-size: 2rem;
`;

const UploadLabel = styled.label`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(0, 0, 0, 0.5);
	color: white;
	opacity: 0;
	transition: opacity 0.3s ease;

	&:hover {
		opacity: 1;
	}
`;

const Range = styled.input`
	width: 100%;
	margin-top: 1rem;
`;

const StyledForm = styled(Form)`
	text-align: left;

	.ant-form-item {
		margin-bottom: 1.5rem;
	}

	.ant-form-item-label {
		padding-bottom: 0.5rem;
	}

	.ant-form-item-label > label {
		font-weight: 500;
		color: #333;
	}

	.ant-input-affix-wrapper {
		height: 40px;
		border-radius: 20px;
		border: 1px solid #d9d9d9;

		&:hover,
		&:focus {
			border-color: ${CUSTOM_COLORS.PRIMARY};
		}
	}

	.ant-input {
		font-size: 0.9rem;
	}
`;

const StartNowButton = styled(Button)`
	height: 40px;
	border-radius: 20px;
	font-size: 0.9rem;
	font-weight: 500;
	background: linear-gradient(135deg, ${CUSTOM_COLORS.PRIMARY} 0%, #764ba2 100%);
	border: none;
	color: white;
	width: 100%;

	&:hover {
		background: linear-gradient(135deg, ${CUSTOM_COLORS.PRIMARY} 0%, #764ba2 100%) !important;
		opacity: 0.9;
	}
`;

const SkipLink = styled.button`
	background: none;
	border: none;
	color: #f72586;
	font-size: 0.875rem;
	font-weight: 500;
	cursor: pointer;
	text-decoration: none;
	position: absolute;
	top: 1rem;
	right: 1rem;

	&:hover {
		color: #f72586;
		opacity: 0.8;
	}
`;

const UsernameInputContainer = styled.div`
	position: relative;
	height: 40px;
	border-radius: 20px;
	border: 1px solid #d9d9d9;
	background: white;
	display: flex;
	align-items: center;
	padding: 0 12px;

	&:hover,
	&:focus-within {
		border-color: ${CUSTOM_COLORS.PRIMARY};
	}

	.prefix-text {
		color: #999;
		font-size: 0.9rem;
		margin-right: 4px;
		pointer-events: none;
	}

	.username-input {
		border: none;
		outline: none;
		background: transparent;
		font-size: 0.9rem;
		padding: 0;
		margin: 0;
		flex: 1;
		&::placeholder {
			color: #999;
		}
	}
`;

const UpdateUser: React.FC = () => {
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const editorRef = useRef<AvatarEditor | null>(null);
	const [loading, setLoading] = useState(false);
	const [image, setImage] = useState<File | null>(null);
	const [scale, setScale] = useState(1.2);
	const [name, setName] = useState("");
	const [isCheckingName, setIsCheckingName] = useState(false);
	const [nameExists, setNameExists] = useState<boolean | null>(null);
	const [isNameValid, setIsNameValid] = useState(false);

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0] || null;
		setImage(file);
	};

	const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setName(value);
	};

	// Debounce name availability check
	useEffect(() => {
		if (!name) {
			setNameExists(null);
			setIsNameValid(false);
			return;
		}

		const isValid = /^[a-zA-Z0-9_]{3,20}$/.test(name);
		setIsNameValid(isValid);
		if (!isValid) {
			setNameExists(null);
			return;
		}

		const handle = setTimeout(async () => {
			try {
				setIsCheckingName(true);
				const res = await userService.checkUsernameExists(name);
				setNameExists(Boolean(res?.username));
			} catch (_) {
				setNameExists(null);
			} finally {
				setIsCheckingName(false);
			}
		}, 500); // 500ms debounce
		return () => clearTimeout(handle);
	}, [name]);

	const handleSkip = () => {
		navigate("/");
	};

	const onFinish = async (values: any) => {
		setLoading(true);
		try {
			let profileImage: string | undefined = undefined;

			// If user selected a new image, get the cropped version
			if (image && editorRef.current) {
				const canvas = editorRef.current.getImageScaledToCanvas();
				profileImage = canvas.toDataURL("image/png");
			}

			// Update user profile
			await authenticationService.updateUser({
				name: values.name,
				profileImage: profileImage,
			});

			message.success("Profile updated successfully!");
			navigate("/");
		} catch (error: any) {
			const errorMsg = error.response?.data?.message || "Failed to update profile. Please try again.";
			message.error(errorMsg);
		} finally {
			setLoading(false);
		}
	};

	return (
		<AuthLayout
			title="Tell us more about you"
			subtitle="Enter your name and upload a profile photo"
			isOrganization={true}
		>
			<SkipLink onClick={handleSkip}>Skip</SkipLink>

			<UpdateContainer>
				<StyledForm form={form} name="updateUser" onFinish={onFinish} layout="vertical">
					<AvatarSection>
						<AvatarEditorWrapper>
							{image ? (
								<AvatarEditor
									ref={editorRef}
									image={image}
									width={120}
									height={120}
									border={0}
									borderRadius={60}
									scale={scale}
								/>
							) : (
								<DefaultAvatar>
									<UserOutlined />
								</DefaultAvatar>
							)}
							<UploadLabel htmlFor="avatar-input">{image ? "Change photo" : "Upload photo"}</UploadLabel>
							<input
								id="avatar-input"
								type="file"
								accept="image/*"
								style={{ display: "none" }}
								onChange={handleFileChange}
							/>
						</AvatarEditorWrapper>

						{image && (
							<Range
								type="range"
								min="1"
								max="3"
								step="0.1"
								value={scale}
								onChange={(e) => setScale(parseFloat(e.target.value))}
							/>
						)}
					</AvatarSection>

					<Form.Item
						name="name"
						label="Name"
						validateStatus={nameExists === true ? "error" : isCheckingName ? "validating" : undefined}
						help={nameExists === true ? "Name already taken" : undefined}
						rules={[
							{ required: true, message: "Please enter your name!" },
							{ min: 3, message: "Name must be at least 3 characters!" },
							{ max: 20, message: "Name must be less than 20 characters!" },
							{
								pattern: /^[a-zA-Z0-9_]+$/,
								message: "Name can only contain letters, numbers, and underscores!",
							},
						]}
					>
						<UsernameInputContainer>
							<UserOutlined style={{ marginRight: "8px", color: "#999" }} />
							<span className="prefix-text">thecreatorverse.co/</span>
							<input
								className="username-input"
								placeholder="name"
								value={name}
								onChange={handleNameChange}
							/>
						</UsernameInputContainer>
					</Form.Item>

					<Form.Item>
						<StartNowButton
							type="primary"
							htmlType="submit"
							loading={loading || isCheckingName}
							disabled={!isNameValid || isCheckingName || nameExists === true}
						>
							Start now
						</StartNowButton>
					</Form.Item>
				</StyledForm>
			</UpdateContainer>
		</AuthLayout>
	);
};

export default UpdateUser;
